/*************************************************************************
 *
 * KLEARNOW CONFIDENTIAL
 * __________________
 *
 *  Copyright (c) 2018 - 2020 KLEARNOW Corporation.
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of KLEARNOW Corporation and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to KLEARNOW Corporation
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from KLEARNOW Corporation.
 */

import { flatten } from 'lodash';
import isEqual from 'lodash/isEqual';
import { createSelector } from 'reselect';
import { defaultInvoiceUIState, invoiceKeyFromInvoice } from '../reducer/MerchandiseUIReducer';

// Base selector
const selectDescriptionOfMerchandiseState = (state) => state.DescriptionOfMerchandise;
const selectMerchandiseUIState = (state) => state.MerchandiseUI;

// Memoized Merchandise selectors
export const selectDescriptionOfMerchandise = createSelector(
  [selectDescriptionOfMerchandiseState],
  (merchandise) => merchandise
);

export const selectEntrySummaryInvoices = createSelector(
  [selectDescriptionOfMerchandiseState],
  (merchandise) => merchandise?.entrySummaryInvoices || []
);

export const selectMerchandiseLineItems = createSelector(
  [selectDescriptionOfMerchandiseState],
  (merchandise) => merchandise?.merchandiseLineItems
);

export const selectTariffsLoader = createSelector(
  [selectDescriptionOfMerchandiseState],
  (merchandise) => merchandise?.tariffsLoader
);

export const selectReferenceType = createSelector(
  [selectDescriptionOfMerchandiseState],
  (merchandise) => merchandise?.referenceType
);

export const selectReferenceNumber = createSelector(
  [selectDescriptionOfMerchandiseState],
  (merchandise) => merchandise?.referenceNumber
);

export const selectUseAggregated = createSelector(
  [selectDescriptionOfMerchandiseState],
  (merchandise) => merchandise?.useAggregated
);

export const selectTotalLineItemWeight = createSelector(
  [selectDescriptionOfMerchandiseState],
  (merchandise) => merchandise?.totalLineItemWeight
);

export const selectMpf = createSelector(
  [selectDescriptionOfMerchandiseState],
  (merchandise) => merchandise?.mpf
);

export const selectHmf = createSelector(
  [selectDescriptionOfMerchandiseState],
  (merchandise) => merchandise?.hmf
);

// Parameterized selectors (returns a function that creates a selector)
export const selectInvoiceByIndex = (invoiceItemIndex) => createSelector(
  [selectEntrySummaryInvoices],
  (invoices) => invoices?.[invoiceItemIndex]
);

export const selectInvoiceIdByIndex = (invoiceItemIndex) => createSelector(
  [selectEntrySummaryInvoices],
  (invoices) => invoices?.[invoiceItemIndex]?.invoiceId
);

export const selectMerchandiseLineItemByIndex = (invoiceItemIndex, lineItemIndex) => createSelector(
  [selectEntrySummaryInvoices],
  (invoices) => invoices?.[invoiceItemIndex]?.merchandiseLineItems?.[lineItemIndex]
);

const EMPTY_LINE_ITEMS = [];

export const selectMerchandiseLineItemsByInvoiceIndex = (invoiceItemIndex) => createSelector(
  [selectEntrySummaryInvoices],
  (invoices) => invoices?.[invoiceItemIndex]?.merchandiseLineItems || EMPTY_LINE_ITEMS
);

const extractLineManifestEntry = (item) => ({
  merchandiseId: item.merchandiseId,
  articleHeader: item.articleHeader,
  articleSetId: item.articleSetId,
  seqNo: item.seqNo,
  articleSubComponent: item.articleSubComponent,
  originalMerchandiseId: item.originalMerchandiseId,
  htsus: item.merchandiseDuties?.[0]?.htsus,
});

/** Stable array ref when only nested line field values change (not structure). */
export const selectStableLineManifestByInvoiceIndex = (invoiceItemIndex) => {
  let cachedManifest = null;
  return createSelector(
    [selectMerchandiseLineItemsByInvoiceIndex(invoiceItemIndex)],
    (items) => {
      const next = (items || []).map(extractLineManifestEntry);
      if (cachedManifest && isEqual(cachedManifest, next)) {
        return cachedManifest;
      }
      cachedManifest = next;
      return next;
    }
  );
};

export const selectMerchandiseLineCountByInvoiceIndex = (invoiceItemIndex) => createSelector(
  [selectMerchandiseLineItemsByInvoiceIndex(invoiceItemIndex)],
  (items) => items?.length || 0
);

/** Stable ref for PSC bulk copy — identity fields only. */
export const selectPscLineTargetsByInvoiceIndex = (invoiceItemIndex) => {
  let cachedTargets = null;
  return createSelector(
    [selectMerchandiseLineItemsByInvoiceIndex(invoiceItemIndex)],
    (items) => {
      const next = (items || []).map((item) => ({ merchandiseId: item.merchandiseId }));
      if (cachedTargets && isEqual(cachedTargets, next)) {
        return cachedTargets;
      }
      cachedTargets = next;
      return next;
    }
  );
};

// Memoized selector for unique merchandise items
export const selectUniqueMerchandiseItemsByInvoiceIndex = (invoiceItemIndex) => createSelector(
  [selectMerchandiseLineItemsByInvoiceIndex(invoiceItemIndex)],
  (merchandiseLineItems) => {
    // Import uniq locally to avoid circular dependencies
    const uniq = require('lodash/uniq');
    return uniq(merchandiseLineItems);
  }
);

export const selectMerchandiseDutyByIndex = (invoiceItemIndex, lineItemIndex, dutyIndex) => createSelector(
  [selectEntrySummaryInvoices],
  (invoices) => invoices?.[invoiceItemIndex]?.merchandiseLineItems?.[lineItemIndex]?.merchandiseDuties?.[dutyIndex]
);

export const selectCurrencyExchangeInfoByIndex = (invoiceItemIndex) => createSelector(
  [selectEntrySummaryInvoices],
  (invoices) => invoices?.[invoiceItemIndex]?.currencyExchangeInfo
);

export const selectInvoiceAmountByIndex = (invoiceItemIndex) => createSelector(
  [selectEntrySummaryInvoices],
  (invoices) => invoices?.[invoiceItemIndex]?.invoiceAmount
);

export const selectInvoiceNumberByIndex = (invoiceItemIndex) => createSelector(
  [selectEntrySummaryInvoices],
  (invoices) => invoices?.[invoiceItemIndex]?.invoiceNumber
);

export const selectInvoiceDateByIndex = (invoiceItemIndex) => createSelector(
  [selectEntrySummaryInvoices],
  (invoices) => invoices?.[invoiceItemIndex]?.invoiceDate
);

export const SelectAllSequenceNumbersSorted = createSelector(
  [selectEntrySummaryInvoices],
  (invoices) => {
    // Use Set for O(1) lookups if needed
    const seqNos = new Set();
    (invoices || []).forEach(inv => {
      (inv?.merchandiseLineItems || []).forEach(item => {
        if (item?.seqNo) seqNos.add(item.seqNo);
      });
    });
    return Array.from(seqNos).sort((a, b) => a - b);
  }
);

// Memoized selector for total line items count
export const selectTotalLineItems = createSelector(
  [selectEntrySummaryInvoices],
  (invoices) => {
    return (invoices || []).reduce((total, invoice) => 
      total + (invoice?.merchandiseLineItems?.length || 0), 0
    );
  }
);

// Selector for line item dropdown options (formatted for Semantic UI Dropdown)
export const selectMerchandiseUIByInvoiceIndex = (invoiceItemIndex) => createSelector(
  [selectMerchandiseUIState, selectEntrySummaryInvoices],
  (merchandiseUI, invoices) => {
    const invoice = invoices?.[invoiceItemIndex];
    const key = invoiceKeyFromInvoice(invoice, invoiceItemIndex);
    return merchandiseUI?.byInvoiceId?.[key] || defaultInvoiceUIState();
  }
);

export const selectHasFetchedMetadataByInvoiceIndex = (invoiceItemIndex) => createSelector(
  [selectMerchandiseUIByInvoiceIndex(invoiceItemIndex)],
  (invoiceUI) => invoiceUI.hasFetchedMetadata ?? false
);

export const selectMetadataByMerchandiseForInvoiceIndex = (invoiceItemIndex) => createSelector(
  [selectMerchandiseUIByInvoiceIndex(invoiceItemIndex)],
  (invoiceUI) => invoiceUI.metadataByMerchandise || {}
);

export const selectExpandInvoiceItemByIndex = (invoiceItemIndex) => createSelector(
  [selectMerchandiseUIByInvoiceIndex(invoiceItemIndex)],
  (invoiceUI) => invoiceUI.expandInvoiceItem ?? true
);

export const selectShowLoaderByInvoiceIndex = (invoiceItemIndex) => createSelector(
  [selectMerchandiseUIByInvoiceIndex(invoiceItemIndex)],
  (invoiceUI) => invoiceUI.showLoader ?? false
);

export const selectLineItemDropdownOptions = (invoiceItemIndex) => createSelector(
  [selectMerchandiseLineItemsByInvoiceIndex(invoiceItemIndex)],
  (lineItems) => {
    if (!lineItems || lineItems.length === 0) return [];
    return lineItems.map((lineItem, idx) => ({
      key: lineItem.merchandiseId,
      value: lineItem.merchandiseId,
      text: `Line Item ${idx + 1}${lineItem.productTag ? ' - ' + lineItem.productTag : ''}`
    }));
  }
);